import { gql } from 'apollo-angular';

/**
 * Query for recovery token signature validation.
 */
export const RECOVERY_TOKEN_SIGNATURE_VALIDATION_QUERY = gql`
  query validateRecoverySignature($signature: String!) {
    response: validateRecoverySignature(signature: $signature) {
      isSuccess
      message
    }
  }
`;

/**
 * Query for login token signature validation.
 */
export const LOGIN_TOKEN_SIGNATURE_VALIDATION_QUERY = gql`
  query validateAuthToken($token: String!) {
    response: validateAuthToken(token: $token) {
      isSuccess
      message
    }
  }
`;

/**
 * Query for current user.
 */
export const CURRENT_USER_QUERY = gql`
  {
    response: currentUser {
      isSuccess
      message
      data {
        id
        username
        email
        firstName
        lastName
        image
        roles
        position
        location
        department
        recoveryRequested
        blocked
      }
    }
  }
`;

/**
 * Query for old current user's JWT.
 */
export const OLD_CURRENT_USER_QUERY = gql`
  {
    response: oldCurrentUser {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for current user's boards.
 */
export const CURRENT_USER_BOARDS_QUERY = gql`
  query currentUserBoards($starred: Boolean!) {
    response: currentUserBoards(starred: $starred) {
      isSuccess
      message
      data {
        id
        name
        description
        ownerId
        public
        shared
        createdAt
        groupId
      }
    }
  }
`;

/**
 * Query for board by ID.
 */
export const BOARD_QUERY = gql`
  query boardById($boardId: String!) {
    response: boardById(boardId: $boardId) {
      isSuccess
      message
      data {
        id
        name
        description
        ownerId
        public
        shared
        createdAt
        groupId
      }
    }
  }
`;

/**
 * Query for widgets of the board by ID.
 */
export const BOARD_WIDGETS_QUERY = gql`
  query boardWidgets($boardId: String!) {
    response: boardWidgets(boardId: $boardId) {
      isSuccess
      message
      data {
        id
        title
        boardId
        ownerId
        headerColor
        x
        y
        cols
        rows
        maxRows
        maxCols
      }
    }
  }
`;

/**
 * Query for admin panel access.
 */
export const ADMIN_ACCESS_QUERY = gql`
  {
    response: hasAdminPanel {
      isSuccess
      message
    }
  }
`;

/**
 * Query for admin groups section access.
 */
export const ADMIN_GROUPS_ACCESS_QUERY = gql`
  {
    response: hasAdminGroupsSection {
      isSuccess
      message
    }
  }
`;

/**
 * Query for admin own groups members.
 */
export const ADMIN_CURRENT_GROUPS_MEMBERS = gql`
  query ownGroupMembers($targetGroupId: String!) {
    response: ownGroupMembers(targetGroupId: $targetGroupId) {
      isSuccess
      message
      data {
        id
        username
        email
        firstName
        lastName
        image
        roles
        position
        location
        department
        recoveryRequested
        blocked
      }
    }
  }
`;

/**
 * Query for group roles.
 */
export const GROUP_ROLES_QUERY = gql`
  query groupRoles($targetGroupId: String!) {
    response: groupRoles(targetGroupId: $targetGroupId) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for checking group admins.
 */
export const GROUP_ADMINS_QUERY = gql`
  query isGroupAdmin($targetUserId: String!, $targetGroupId: String!) {
    response: isGroupAdmin(
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
    }
  }
`;

/**
 * Query for getting groups that user is admin of.
 */
export const ADMIN_GROUPS = gql`
  {
    response: adminGroups {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for getting user sessions.
 */
export const USER_SESSIONS = gql`
  query userSessions($targetUserId: String!, $targetGroupId: String!) {
    response: userSessions(
      targetUserId: $targetUserId
      targetGroupId: $targetGroupId
    ) {
      isSuccess
      message
      data {
        lastActiveAt
        createdAt
        deviceName
        userAgent
      }
    }
  }
`;

/**
 * Query for invite id validation.
 */
export const INVITE_ID_VALIDATION = gql`
  query validateInviteId($inviteId: String!) {
    response: validateInviteId(inviteId: $inviteId) {
      isSuccess
      message
    }
  }
`;

/**
 * Query for invite info.
 */
export const INVITE_INFO = gql`
  query inviteInfo($inviteId: String!) {
    response: inviteInfo(inviteId: $inviteId) {
      isSuccess
      message
      data {
        email
        firstName
        lastName
        suggestedUsername
      }
    }
  }
`;

/**
 * Query for default group role.
 */
export const DEFAULT_GROUP_ROLE = gql`
  query defaultGroupRole($targetGroupId: String!) {
    response: defaultGroupRole(targetGroupId: $targetGroupId) {
      isSuccess
      message
      data {
        role
      }
    }
  }
`;

/**
 * Query for boards by group.
 */
export const GROUP_BOARD_QUERY = gql`
  query groupBoards($targetGroupId: String!) {
    response: groupBoards(targetGroupId: $targetGroupId) {
      isSuccess
      message
      data {
        id
        name
        ownerId
        public
        groupId
        description
        shared
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const USER_QUERY = gql`
  query userInfo($targetUserId: String!) {
    response: userInfo(targetUserId: $targetUserId) {
      isSuccess
      message
      data {
        id
        username
        email
        firstName
        lastName
        image
        roles
        position
        location
        department
        recoveryRequested
        blocked
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const CENSUS_DAY_QUERY = gql`
  query censusData(
    $filter: CensusDataFilterInput!
    $bv: Boolean!
    $cv: Boolean!
    $nv: Boolean!
    $vwd: Boolean!
    $vwd_sum: Boolean!
    $drw: Boolean!
    $bv_at: Boolean!
    $cv_at: Boolean!
    $nv_at: Boolean!
    $vwd_at: Boolean!
    $vwd_sum_at: Boolean!
    $drw_at: Boolean!
    $playerId: Boolean!
    $clipReleaseTime: Boolean!
    $title: Boolean!
    $gs: Boolean!
    $category: Boolean!
    $airDate: Boolean!
    $videoLength: Boolean!
    $matching: Boolean!
  ) {
    response: censusData(filter: $filter) {
      isSuccess
      message
      data {
        count
        sum {
          key
          value {
            day
            facts {
              bV
              bV_AT
              vwd_Sum
              vwd_Sum_AT
            }
          }
        }
        data {
          type
          sBN
          title @include(if: $title)
          channel
          channelGroup
          videoID
          episodeIDs
          seasonID
          key
          totalType
          playerIDs @include(if: $playerId)
          clipReleaseTime @include(if: $clipReleaseTime)
          clipReleaseTimeText @include(if: $clipReleaseTime)
          gS @include(if: $gs)
          category @include(if: $category)
          airDate @include(if: $airDate)
          airDateText @include(if: $airDate)
          duration @include(if: $videoLength)
          matching @include(if: $matching)
          facts {
            key
            value {
              facts {
                bV @include(if: $bv)
                nV @include(if: $nv)
                cV @include(if: $cv)
                vwd @include(if: $vwd)
                vwd_Sum @include(if: $vwd_sum)
                drw @include(if: $drw)
                bV_AT @include(if: $bv_at)
                nV_AT @include(if: $nv_at)
                cV_AT @include(if: $cv_at)
                vwd_AT @include(if: $vwd_at)
                vwd_Sum_AT @include(if: $vwd_sum_at)
                drw_AT @include(if: $drw_at)
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const CENSUS_DAY_SUM_QUERY = gql`
  query censusDataSum($filter: CensusDataFilterInput!) {
    response: censusDataSum(filter: $filter) {
      isSuccess
      message
      data {
        bV
        bV_AT
        vWD_Sum
        vWD_Sum_AT
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const NETTO_REPORT_QUERY = gql`
  query nettoReport($id: String!) {
    response: nettoReport(id: $id) {
      isSuccess
      message
      data {
        creatorUserId
        customId
        state
        percent
        runningMs
        result {
          key
          cv
          nv
          bv
          vwd
          drw
          cv_AT
          nv_AT
          bv_AT
          vwd_AT
          vwd_Sum
          vwd_Sum_AT
          drw_AT
          videoDurationSeconds
        }
        results {
          key
          cv
          nv
          bv
          vwd
          drw
          cv_AT
          nv_AT
          bv_AT
          vwd_Sum
          vwd_Sum_AT
          vwd_AT
          drw_AT
          videoDurationSeconds
        }
      }
    }
  }
`;

/**
 * Query for current user's netto reports.
 */
export const NETTO_REPORT_USER_QUERY = gql`
  query userNettoReport($draft: Boolean!, $savedOnly: Boolean!) {
    response: userNettoReport(draft: $draft, savedOnly: $savedOnly) {
      isSuccess
      message
      data {
        data {
          reportId
          searchKeys {
            type
            sBN
            title
            channel
            channelGroup
            videoID
            episodeIDs
            seasonID
            key
            playerIDs
            clipReleaseTime
            gS
            category
            airDate
            duration
            matching
          }
        }
        reports {
          creatorUserId
          customId
          state
          percent
          runningMs
          added
          reportOptions @include(if: $draft) {
            rangeFrom
            rangeUntil
            searchKeys
            keyType
          }
        }
      }
    }
  }
`;

/**
 * Query for current user's netto reports.
 */
export const NETTO_SAVED_REPORT_USER_QUERY = gql`
  {
    response: userSavedNettoReport {
      isSuccess
      message
      data {
        data {
          reportId
          searchKeys {
            type
            sBN
            title
            channel
            channelGroup
            videoID
            episodeIDs
            seasonID
            key
            playerIDs
            clipReleaseTime
            gS
            category
            airDate
            duration
            matching
          }
        }
        reports {
          _id
          creatorUserId
          customId
          state
          percent
          runningMs
          added
          reportOptions {
            rangeFrom
            rangeUntil
            searchKeys
            keyType
          }
          customData {
            path
            name
            description
            savedAt
          }
        }
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const SAVED_GROUP_REPORTS_QUERY = gql`
  query savedGroupReports($groupId: String!) {
    response: savedGroupReports(groupId: $groupId) {
      isSuccess
      message
      data {
        customId
        name
        folderName
        groupId
        data
      }
    }
  }
`;

/**
 * Query for current user.
 */
export const REPORT_FOLDERS_QUERY = gql`
  {
    response: userReportFolders {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for toplist settings.
 */
export const TOPLIST_NG_SETTINGS = gql`
  query toplistSettings($language: String!) {
    response: toplistSettings(language: $language) {
      isSuccess
      message
      data {
        groupName
        groupIdentifier
        type
        default
        multiple
        values {
          friendlyName
          identifier
          renderer
          position
          modes
          showInNettoAggregation
        }
      }
    }
  }
`;

/**
 * Query for census settings.
 */
export const CENSUS_NG_SETTINGS = gql`
  query censusSettings($language: String!) {
    response: censusSettings(language: $language) {
      isSuccess
      message
      data {
        groupName
        groupIdentifier
        type
        default
        values {
          friendlyName
          identifier
          renderer
          position
          modes
          showInNettoAggregation
        }
      }
    }
  }
`;

/**
 * Query for census filters.
 */
export const CENSUS_NG_FILTERS = gql`
  query censusFilters($language: String!) {
    response: censusFilters(language: $language) {
      isSuccess
      message
      data {
        checkRequired
        friendlyName
        identifier
        type
        values {
          friendlyName
          identifier
        }
        sortField
      }
    }
  }
`;

/**
 * Query for data availability info.
 */
export const CENSUS_NG_AVAILABLE_DAYS = gql`
  query {
    response: censusDataAvailabilityInfo {
      isSuccess
      message
      data {
        lastAvailableDay
      }
    }
  }
`;

/**
 * Query for data availability info.
 */
export const CENSUS_NG_SMART_VIEW_AVAILABLE_DAYS = gql`
  query {
    response: censusSmartViewDataAvailabilityInfo {
      isSuccess
      message
      data {
        firstAvailableDay
        lastAvailableDay
      }
    }
  }
`;

/**
 * Query for census data.
 */
export const CENSUS_NG_DATA = gql`
  query censusNgData(
    $page: Int!
    $pageLimit: Int!
    $range: Boolean!
    $from: LocalDate!
    $to: LocalDate!
    $sortField: String!
    $characteristics: CensusFilterBaseInput!
    $facts: CensusFilterBaseInput!
    $filters: [CensusFilterBaseInput]!
    $dataLimit: Int!
    $includeSum: Boolean!
  ) {
    response: censusNgData(
      page: $page
      pageLimit: $pageLimit
      range: $range
      from: $from
      to: $to
      sortField: $sortField
      characteristics: $characteristics
      facts: $facts
      filters: $filters
      dataLimit: $dataLimit
      includeSum: $includeSum
    ) {
      isSuccess
      message
      data {
        count
        infoRows
        sum {
          key
          value {
            day
            facts {
              key
              value {
                ats
                reachPnr
                reachPnrPct
                visitDuration
              }
            }
          }
        }
        data {
          title
          channel
          mediaPlayer
          aggregationLevel
          key
          facts {
            key
            value {
              facts {
                key
                value {
                  ats
                  reachPnr
                  reachPnrPct
                  visitDuration
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for data availability info.
 */
export const TOPLIST_NG_AVAILABLE_DAYS = gql`
  query toplistDataAvailabilityInfo($range: ToplistRange!) {
    response: toplistDataAvailabilityInfo(range: $range) {
      isSuccess
      message
      data {
        lastAvailableDay
      }
    }
  }
`;

/**
 * Query for toplist access.
 */
export const TOPLIST_NG_ACCESS = gql`
  query {
    response: toplistsAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const UNPUBLISHED_TOPLIST_NG_ACCESS = gql`
  query {
    response: toplistsUnpublishedAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for toplist data.
 */
export const TOPLIST_NG_DATA = gql`
  query toplistNgData(
    $from: LocalDate!
    $range: ToplistRange!
    $channels: [String]!
    $cliptype: String!
    $showPreview: Boolean!
  ) {
    response: toplistNgData(
      from: $from
      range: $range
      channels: $channels
      cliptype: $cliptype
      showPreview: $showPreview
    ) {
      isSuccess
      message
      data {
        count
        isPreview
        infoText
        sourceText
        dateText
        dataState
        data {
          groupPosition
          group
          broadcastType
          position
          channel
          cliptype
          sBN
          seasonID
          duration
          drw_AT
          bV_AT
        }
      }
    }
  }
`;

/**
 * Query for exporting toplist data.
 */
export const TOPLIST_EXPORT_NG_DATA = gql`
  query exportToplistNgData(
    $from: LocalDate!
    $range: ToplistRange!
    $channels: [String]!
    $cliptype: String!
  ) {
    response: exportToplistNgData(
      from: $from
      range: $range
      channels: $channels
      cliptype: $cliptype
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for exporting research toplist data.
 */
export const RESEARCH_EXPORT_TOPLIST_NG_DATA = gql`
  query exportToplistResearchNgData(
    $from: LocalDate!
    $to: LocalDate!
    $range: ToplistRange!
    $channels: [String]!
    $cliptype: String!
    $searchQuery: String!
    $sortField: String!
  ) {
    response: exportToplistResearchNgData(
      from: $from
      to: $to
      range: $range
      channels: $channels
      cliptype: $cliptype
      searchQuery: $searchQuery
      sortField: $sortField
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for research toplist data.
 */
export const RESEARCH_TOPLIST_NG_DATA = gql`
  query toplistResearchNgData(
    $from: LocalDate!
    $to: LocalDate!
    $range: ToplistRange!
    $channels: [String]!
    $cliptype: String!
    $searchQuery: String!
    $sortField: String!
  ) {
    response: toplistResearchNgData(
      from: $from
      to: $to
      range: $range
      channels: $channels
      cliptype: $cliptype
      searchQuery: $searchQuery
      sortField: $sortField
    ) {
      isSuccess
      message
      data {
        count
        infoText
        sourceText
        dateText
        dataState
        data {
          groupPosition
          group
          broadcastType
          position
          channel
          cliptype
          sBN
          seasonID
          duration
          drw_AT
          bV_AT
        }
      }
    }
  }
`;

/**
 * Query for census data exporting.
 */
export const CENSUS_NG_DATA_EXPORT = gql`
  query exportCensusNgData(
    $page: Int!
    $pageLimit: Int!
    $range: Boolean!
    $from: LocalDate!
    $to: LocalDate!
    $sortField: String!
    $characteristics: CensusFilterBaseInput!
    $facts: CensusFilterBaseInput!
    $filters: [CensusFilterBaseInput]!
    $dataLimit: Int!
    $includeSum: Boolean!
  ) {
    response: exportCensusNgData(
      page: $page
      pageLimit: $pageLimit
      range: $range
      from: $from
      to: $to
      sortField: $sortField
      characteristics: $characteristics
      facts: $facts
      filters: $filters
      dataLimit: $dataLimit
      includeSum: $includeSum
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for netto report export.
 */
export const CENSUS_NG_NETTO_REPORT_EXPORT = gql`
  query exportCensusNgNettoAgg(
    $reportId: String!
    $key: KeyType!
    $characteristics: CensusFilterBaseInput!
    $facts: CensusFilterBaseInput!
  ) {
    response: exportCensusNgNettoAgg(
      reportId: $reportId
      key: $key
      characteristics: $characteristics
      facts: $facts
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for getting netto report by id.
 */
export const CENSUS_NG_REPORT_BY_ID = gql`
  query censusNgReportById($reportId: String!) {
    response: censusNgReportById(reportId: $reportId) {
      isSuccess
      message
      data {
        reportId
        key
        result {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          drw_AT
          vwd_Sum_AT
          vwd_Sum
          duration
        }
        results {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          vwd_Sum
          vwd_Sum_AT
          drw_AT
          type
          sBN
          title
          channel
          channelGroup
          videoID
          episodeIDs
          seasonID
          key
          playerIDs
          clipReleaseTime
          clipReleaseTimeText
          gS
          category
          airDate
          airDateText
          matching
          duration
          totalType
        }
        status {
          added
          percentage
          reportId
          statusText
        }
      }
    }
  }
`;

/**
 * Query for getting netto report by id.
 */
export const CENSUS_NG_DRAFT_REPORT = gql`
  {
    response: censusNgDraftReport {
      isSuccess
      message
      data {
        reportId
        key
        result {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          drw_AT
          duration
        }
        results {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          drw_AT
          type
          sBN
          title
          channel
          channelGroup
          videoID
          episodeIDs
          seasonID
          key
          playerIDs
          clipReleaseTime
          clipReleaseTimeText
          gS
          category
          airDate
          airDateText
          matching
          duration
        }
        status {
          added
          percentage
          reportId
          statusText
        }
      }
    }
  }
`;

/**
 * Query for census folders.
 */
export const CENSUS_NG_NETTO_FOLDERS = gql`
  {
    response: censusNgNettoFolders {
      isSuccess
      message
      data {
        folderName
        id
        template
        owner
        groupId
        allowRename
        allowDelete
      }
    }
  }
`;

/**
 * Query for getting netto reports in folder.
 */
export const CENSUS_NG_REPORTS_IN_FOLDER = gql`
  query censusNgFolderReports($folderId: String!) {
    response: censusNgFolderReports(folderId: $folderId) {
      isSuccess
      message
      data {
        reportId
        key
        customData {
          dashboardUserId
          folderId
          groupId
          title
          description
        }
        result {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          drw_AT
          vwd_Sum_AT
          vwd_Sum
          duration
        }
        results {
          bV
          nV
          cV
          vwd
          drw
          bV_AT
          nV_AT
          cV_AT
          vwd_AT
          vwd_Sum
          vwd_Sum_AT
          drw_AT
          type
          sBN
          title
          channel
          channelGroup
          videoID
          episodeIDs
          seasonID
          key
          playerIDs
          clipReleaseTime
          clipReleaseTimeText
          gS
          category
          airDate
          airDateText
          matching
          duration
          totalType
        }
        status {
          added
          percentage
          reportId
          statusText
        }
      }
    }
  }
`;

/**
 * Query for all user.
 */
export const ALL_USER_QUERY = gql`
  {
    response: dashboardMembers {
      isSuccess
      message
      data {
        id
        username
        email
        firstName
        lastName
        image
        roles
        position
        location
        department
        recoveryRequested
        blocked
      }
    }
  }
`;

/**
 * Query for getting dashboard components.
 */
export const DASHBOARD_COMPONENTS_QUERY = gql`
  {
    response: dashboardComponents {
      isSuccess
      message
      data {
        id
        identifier
        friendlyName
        workflow {
          workflowId
          friendlyName
          identifier
          claims {
            claimId
            identifier
          }
        }
      }
    }
  }
`;

/**
 * Query for getting group components.
 */
export const GROUP_COMPONENTS_QUERY = gql`
  query groupComponents($targetGroupId: String!) {
    response: groupComponents(targetGroupId: $targetGroupId) {
      isSuccess
      message
      data {
        groupId
        components
      }
    }
  }
`;

/**
 * Query for getting component by id.
 */
export const GROUP_COMPONENTS_BY_ID_QUERY = gql`
  query componentById($targetGroupId: String, $componentId: String!) {
    response: componentById(
      targetGroupId: $targetGroupId
      componentId: $componentId
    ) {
      isSuccess
      message
      data {
        id
        identifier
        friendlyName
        workflow {
          workflowId
          friendlyName
          identifier
          claims {
            claimId
            identifier
          }
        }
      }
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const BOARDS_NG_ACCESS = gql`
  query {
    response: boardsAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const REPORTS_NG_ACCESS = gql`
  query {
    response: reportsAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const REALTIME_REPORTS_NG_ACCESS = gql`
  query {
    response: realtimeReportsAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const CENSUS_NG_ACCESS = gql`
  query {
    response: censusAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const CENSUS_NG_SMART_ACCESS = gql`
  query {
    response: censusSmartAccess {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for unpublished toplist access.
 */
export const CENSUS_NG_SMART_VIEW_CHANNELS = gql`
  query {
    response: censusNgSmartViewChannels {
      isSuccess
      message
      data {
        channel
        channelGroup
      }
    }
  }
`;

/**
 * Query for getting smart view data.
 */
export const CENSUS_NG_SMART_VIEW_DATA = gql`
  query censusNgSmartViewData(
    $day: LocalDate!
    $until: LocalDate!
    $channelGroup: [String]
    $channels: [String]
    $programTypes: [SmartViewProgramType!]
    $factTypes: [SmartViewFactType!]
    $query: String
    $sortField: String
  ) {
    response: censusNgSmartViewData(
      day: $day
      until: $until
      channelGroup: $channelGroup
      channels: $channels
      programTypes: $programTypes
      factTypes: $factTypes
      query: $query
      sortField: $sortField
    ) {
      isSuccess
      message
      data {
        isCompleted
        progress
        rowCount
        rows {
          day
          video
          videoLive
          videoVod
          senderGruppe
          sender
          title
          time
          airDate
          programType
          duration
          facts {
            key
            value {
              drwLive
              drwVod
              drwSum
              drwSum_AT
              drwLive_AT
              drwVod_AT
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for getting smart view data.
 */
export const CENSUS_NG_SMART_VIEW_DETAIL_DATA = gql`
  query censusNgSmartViewDetailData(
    $video: Long!
    $videoLive: Long!
    $videoVod: Long!
    $factTypes: [SmartViewFactType!]
  ) {
    response: censusNgSmartViewDetailData(
      video: $video
      videoLive: $videoLive
      videoVod: $videoVod
      factTypes: $factTypes
    ) {
      isSuccess
      message
      data {
        detailFacts {
          key
          value {
            key
            value {
              drwLive
              drwVod
              drwSum
              drwSum_AT
              drwLive_AT
              drwVod_AT
            }
          }
        }
      }
    }
  }
`;

/**
 * Query for ¸exporting smart view data.
 */
export const CENSUS_NG_SMART_VIEW_EXPORT = gql`
  query censusNgSmartViewExport(
    $day: LocalDate!
    $until: LocalDate!
    $channelGroup: [String]
    $channels: [String]
    $programTypes: [SmartViewProgramType!]
    $factTypes: [SmartViewFactType!]
    $query: String
    $sortField: String
  ) {
    response: censusNgSmartViewExport(
      day: $day
      until: $until
      channelGroup: $channelGroup
      channels: $channels
      programTypes: $programTypes
      factTypes: $factTypes
      query: $query
      sortField: $sortField
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for ¸exporting smart view data.
 */
export const CENSUS_NG_SMART_VIEW_DETAILS_EXPORT = gql`
  query censusNgSmartViewDetailsExport(
    $video: Long!
    $videoLive: Long!
    $videoVod: Long!
    $factTypes: [SmartViewFactType!]
  ) {
    response: censusNgSmartViewDetailsExport(
      video: $video
      videoLive: $videoLive
      videoVod: $videoVod
      factTypes: $factTypes
    ) {
      isSuccess
      message
      data
    }
  }
`;

/**
 * Query for data availability info.
 */
export const CENSUS_NG_SMART_VIEW_INFO_MESSAGE = gql`
  query {
    response: censusNgSmartViewMessage {
      isSuccess
      message
      data {
        title
        message
        messageType
      }
    }
  }
`;
